// Recent items tracking for command palette
import { setPref } from './userPreferences'

export interface RecentItem {
  id: string
  type: 'athlete' | 'prospect' | 'deal' | 'contact' | 'contract' | 'task'
  name: string
  timestamp: number
}

const STORAGE_KEY = 'dealpoint_recent_items'
const MAX_ITEMS = 8

export function getRecentItems(): RecentItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []
    const items = JSON.parse(stored) as RecentItem[]
    // Sort by timestamp descending (most recent first)
    return items.sort((a, b) => b.timestamp - a.timestamp)
  } catch (error: any) {
    throw new Error(`localStorage error: ${error.message}`)
  }
}

export function addRecentItem(
  type: RecentItem['type'],
  id: string,
  name: string
): void {
  try {
    const items = getRecentItems()

    // Remove existing item with same id and type if present
    const filtered = items.filter(item => !(item.id === id && item.type === type))

    // Add new item at the beginning
    const newItem: RecentItem = {
      id,
      type,
      name,
      timestamp: Date.now()
    }

    const updated = [newItem, ...filtered].slice(0, MAX_ITEMS)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    setPref('recent_items', updated)
  } catch (error: any) {
    throw new Error(`localStorage error: ${error.message}`)
  }
}

export function clearRecentItems(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    setPref('recent_items', [])
  } catch (error: any) {
    throw new Error(`localStorage error: ${error.message}`)
  }
}

// Get route for a recent item
export function getRecentItemRoute(item: RecentItem): string {
  switch (item.type) {
    case 'athlete':
      return `/clients/${item.id}`
    case 'prospect':
      return `/prospects/${item.id}`
    case 'deal':
      return `/deals/${item.id}`
    case 'contact':
      return `/contacts/${item.id}`
    case 'contract':
      return `/contracts/${item.id}`
    case 'task':
      return `/tasks/${item.id}`
    default:
      return '/'
  }
}

// Get icon name for a recent item type
export function getRecentItemIcon(type: RecentItem['type']): string {
  switch (type) {
    case 'athlete':
      return 'user'
    case 'prospect':
      return 'user-plus'
    case 'deal':
      return 'file-text'
    case 'contact':
      return 'users'
    case 'contract':
      return 'file'
    case 'task':
      return 'check-square'
    default:
      return 'file'
  }
}
